import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import 'react-toastify/dist/ReactToastify.css';
import UserCards from "./UserCards";

const Chat = ({ mode, initialMessages = [], onSendMessage, loggedIn, placeholder }) => {
    const [message, setMessage] = useState("");
    const [chat, setChat] = useState(initialMessages);
    const inputRef = useRef(null);
    const bottomRef = useRef(null);
    const { userId } = useAuth();
    const [isLoggedIn, setIsLoggedIn] = useState(loggedIn);
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();

    const handleSend = async () => {
        if (!message.trim()) return;

        const userMessage = { from: "user", text: message };

        // ❗ Remove starter once user sends first message
        const filteredChat =
            chat.length === 1 && chat[0].from === "ai" ? [] : chat;

        const updatedChat = [...filteredChat, userMessage];

        setChat(updatedChat);
        setMessage("");

        if (onSendMessage) {
            setIsLoading(true);

            // ⏳ temporary loading bubble
            setChat((prev) => [
                ...prev,
                { from: "ai", text: "", loading: true }
            ]);

            try {
                const aiResponse = await onSendMessage(message, updatedChat);

                setChat((prev) => {
                    // remove loading bubble
                    const withoutLoading = prev.filter(msg => !msg.loading);

                    if (!aiResponse) return withoutLoading;

                    return [
                        ...withoutLoading,
                        {
                            from: "ai",
                            text: aiResponse.aiResponse,
                            freelancers: aiResponse.freelancers,
                            businesses: aiResponse.businesses,
                            jobs: aiResponse.jobs || []
                        }
                    ];
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        setIsLoggedIn(loggedIn);
    }, [loggedIn]);

    const handleProfileClick = (type, id) => {

        if (type === "freelancer") {
            navigate(`/freelancer/${id}`);
        }
        if (type === "business") {
            navigate(`/business/${id}`);
        }
    };

    const formatLocation = (city, state, country = "US") => {
        if (!city && !state) return null;

        const parts = [];
        if (city) parts.push(city);
        if (state) parts.push(state);

        if (country && country !== "US") {
            parts.push(country);
        }

        return parts.join(", ");
    };

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [chat]);

    return (
        <div className={`chat-main-container ${loggedIn ? "landing-chat" : ""}`}>
            <div className="chat-box-wrapper">

                <div className={`chat-box ${chat.length === 1 && chat[0].from === "ai" ? "starter-only" : ""}`}>
                    {chat.map((msg, index) => {
                        const isStarter = index === 0 && msg.from === "ai" && chat.length === 1;

                        return (
                            <div
                                key={index}
                                className={`chat-row ${msg.from} ${isStarter ? "starter" : ""}`}
                            >
                                <div className={`chat-bubble ${msg.from} ${isStarter ? "starter" : ""}`}>

                                    {msg.loading && (
                                        <div className="typing-indicator">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    )}

                                    {!msg.loading && <div className="ai-text">{msg.text}</div>}

                                    {/* only show cards in user mode */}
                                    {mode === "user" && (
                                        <UserCards
                                            msg={msg}
                                            onProfileClick={handleProfileClick}
                                            formatLocation={formatLocation}
                                        />
                                    )}

                                    {msg.jobs?.length > 0 && (
                                        <div className="provider-results">
                                            {msg.jobs.map(job => (
                                                <div
                                                    key={job.id}
                                                    className="provider-card"
                                                    onClick={() => navigate(`/home/jobs/${job.id}`)}
                                                >
                                                    <div className="provider-card-inner">
                                                        {/* Placeholder image */}
                                                        <div className="provider-image">
                                                            {job.image ? (
                                                                <img src={job.image} alt={job.title} />
                                                            ) : (
                                                                <div className="chat-provider-image-placeholder">
                                                                    No Image
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Info */}
                                                        <div className="provider-info">
                                                            <div className="provider-title">{job.title}</div>

                                                            {(job.city || job.state) && (
                                                                <div className="provider-location">
                                                                    {job.city}{job.city && job.state ? ", " : ""}{job.state}
                                                                </div>
                                                            )}

                                                            <div className="provider-summary">{job.summary}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <div ref={bottomRef} />
                </div>
            </div>

            <div className="chat-input-container">
                <input
                    ref={inputRef}
                    type="text"
                    value={message}
                    placeholder={placeholder || "Type your message..."}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                />
                <button className="search-btn" onClick={handleSend}>
                    <i className="fa-solid fa-arrow-up"></i>
                </button>
            </div>
        </div>
    );
};

export default Chat;